import blocksToHtml from '@sanity/block-content-to-html'

import {
  SanityProductFragment,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { getFirstValue, getPrice } from './helpers'
import { getSanityImageUrl } from './image'
import { getProductGalleryPhotos } from './product'
import { ProductReview, ProductReviewSummary } from './review'
import { PageType, getPageUrl } from './routes'

interface AuthorSchema {
  '@type': 'Person' | 'Organization'
  name?: string
  url?: string
}

interface BrandSchema {
  '@type': 'Brand'
  name: string
}

interface ImageSchema {
  '@type': 'ImageObject'
  '@id': string
  url: string
  width?: string
  height?: string
}

interface OfferSchema {
  '@type': 'Offer'
  url: string
  availability: string
  price?: string
  priceCurrency?: string
  priceValidUntil?: string
}

interface ReviewSchema {
  '@type': 'Review'
  reviewRating: {
    '@type': 'Rating'
    ratingValue?: number
  }
  author: string | AuthorSchema
  reviewBody?: string
}

interface AggregateRatingSchema {
  '@type': 'AggregateRating'
  ratingCount: string
  ratingValue: string
}

interface ProductSchema {
  '@context': 'https://schema.org'
  '@type': 'Product'
  name: string
  description?: string
  sku?: string
  gtin?: string
  brand: BrandSchema
  image?: string | string[] | ImageSchema | ImageSchema[]
  offers?: OfferSchema
  review?: ReviewSchema[]
  aggregateRating?: AggregateRatingSchema
}

interface BlogPostSchema {
  '@context': 'https://schema.org'
  '@type': 'BlogPosting'
  url: string
  headline: string
  datePublished: Date
  dateModified: Date
  author?: string | AuthorSchema
  keywords?: string[]
  abstract?: string
  articleBody?: string
  thumbnailUrl?: string
  image?: string | string[] | ImageSchema | ImageSchema[]
}

/**
 * Gets aggregate rating schema.
 */
const getAggregateRatingSchema = (
  ratingCount?: number,
  ratingValue?: number
): AggregateRatingSchema | undefined => {
  if (!ratingCount || !ratingValue) {
    return
  }

  return {
    '@type': 'AggregateRating',
    ratingCount: `${ratingCount}`,
    ratingValue: `${ratingValue}`,
  }
}

/**
 * Gets reviews schema from given reviews.
 */
const getReviewsSchema = (reviews: ProductReview[]) => {
  if (reviews.length === 0) {
    return
  }

  return reviews.map((review) => {
    const reviewSchema: ReviewSchema = {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.reviewRating,
      },
      author: {
        '@type': 'Person',
        name: review.author,
      },
      reviewBody: review.reviewMessage,
    }

    return reviewSchema
  })
}

/**
 * Returns product schema.
 */
export const getProductSchema = (
  productReviews: ProductReview[],
  productReviewSummary: ProductReviewSummary,
  product?: SanityProductFragment,
  variant?: SanityProductVariantFragment,
  siteTitle?: string,
  siteUrl?: string,
  currencyCode?: string,
  taxRate?: number
) => {
  if (!product || !currencyCode || !taxRate) {
    return
  }

  const description = product.description?.length
    ? blocksToHtml({
        blocks: product.description,
      })
    : ''

  const isInStock = variant?.inStock ?? product.inStock
  const price = getPrice(variant?.price ?? product.price, taxRate, false, '')
  const comparePrice = getPrice(
    variant?.comparePrice ?? product.comparePrice,
    taxRate,
    false,
    ''
  )
  const domain = siteUrl?.replace(/\/+$/g, '')
  const productUrl = getPageUrl(PageType.PRODUCT, product.slug, {
    parameters: {
      ...(variant
        ? {
            variant: `${variant?.id}`,
          }
        : {}),
    },
  })
  const nextYearIsoDate = new Date(
    new Date().setFullYear(new Date().getFullYear() + 1)
  )
    .toISOString()
    .split('T')[0]

  const getImage = () => {
    const photos = product.photos.main
      ? getProductGalleryPhotos(product.photos.main, variant)
      : []

    if (photos.length === 0) {
      return
    }

    return getSanityImageUrl(photos[0], {
      width: 800,
      fitMax: true,
      autoFormat: true,
    })
  }

  const productSchema: ProductSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    description,
    sku: getFirstValue([variant?.sku, product.sku]),
    gtin: getFirstValue([variant?.barcode, product.barcode]),
    brand: {
      '@type': 'Brand',
      name: siteTitle ?? '',
    },
    image: getImage(),
    offers: {
      '@type': 'Offer',
      availability: isInStock
        ? 'http://schema.org/InStock'
        : 'http://schema.org/SoldOut',
      price,
      priceCurrency: currencyCode,
      url: `${domain}${productUrl}`,
      priceValidUntil: nextYearIsoDate,
    },
    review: getReviewsSchema(productReviews),
    aggregateRating: getAggregateRatingSchema(
      productReviewSummary.ratingCount,
      productReviewSummary.ratingValue
    ),
  }

  return JSON.stringify(productSchema)
}
